"use client";

import { useEffect, useState } from "react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  getRecentActivity,
  type AnalyticsEvent,
} from "@/services/analytics.service";
import { useOrganization } from "@/context/OrganizationContext";
import { PermissionNotice } from "@/components/permission-notice";

interface ActivitiesProps {
  compact?: boolean;
  events?: Array<{
    id: string;
    timestamp: string;
    eventType: string;
    user: string;
    status: "success" | "failed" | "pending";
    details: string;
  }>;
  title?: string;
  description?: string;
  filterEventType?: string;
}

function toInitials(nameOrEmail: string) {
  const emailName = nameOrEmail.includes("@")
    ? nameOrEmail.split("@")[0]
    : nameOrEmail;
  const parts = emailName
    .replace(/[^a-zA-Z ]/g, " ")
    .trim()
    .split(/\s+/);
  if (parts.length === 1) {
    const p = parts[0];
    return (p[0] || "?").toUpperCase();
  }
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

function mapStatus(status: string) {
  if (status === "failed") return "error";
  if (status === "pending") return "warning";
  return status;
}

const merchantLabels: Record<string, string> = {
  IDENTITY_SUGGESTIONS: "identity suggestions",
  CATEGORY_SUGGESTIONS: "category suggestions",
  ATTRIBUTES_SUGGESTIONS: "attribute suggestions",
  DESCRIPTIONS_SUGGESTIONS: "description suggestions",
  IMAGES_MODERATION: "image moderation",
  ASSETS_UPLOAD_URL: "upload URL",
};
const merchantStates: Record<string, { verb: string; status: string }> = {
  STARTED: { verb: "started", status: "success" },
  SUCCEEDED: { verb: "completed", status: "success" },
  FAILED: { verb: "failed", status: "error" },
};
const merchantEventPattern = /^YOUZU_API:MERCHANT_API_(.+)_(STARTED|SUCCEEDED|FAILED)$/;

function formatEventType(eventType: string): {
  action: string;
  initials: string;
  status: string;
} {
  const merchantEvent = merchantEventPattern.exec(eventType);
  if (merchantEvent) {
    const state = merchantStates[merchantEvent[2]];
    const label = merchantLabels[merchantEvent[1]] ?? merchantEvent[1].replace(/_/g, " ").toLowerCase();
    return {
      action: `Merchant API ${label} ${state.verb}`,
      initials: "MA",
      status: state.status,
    };
  }
  const eventMap: Record<
    string,
    { action: string; initials: string; status: string }
  > = {
    "YOUZU_API:2D_TO_3D_CREATE_STARTED": {
      action: "Started 2D to 3D conversion",
      initials: "3D",
      status: "success",
    },
    "YOUZU_API:2D_TO_3D_CREATE_SUCCEEDED": {
      action: "Completed 2D to 3D conversion",
      initials: "3D",
      status: "success",
    },
    "YOUZU_API:2D_TO_3D_CREATE_FAILED": {
      action: "Failed 2D to 3D conversion",
      initials: "3D",
      status: "error",
    },
    "YOUZU_API:OBJECT_DETECTION_STARTED": {
      action: "Started Object Detection",
      initials: "OD",
      status: "success",
    },
    "YOUZU_API:OBJECT_DETECTION_SUCCEEDED": {
      action: "Object Detection Succeeded",
      initials: "OD",
      status: "success",
    },
    "YOUZU_API:OBJECT_DETECTION_FAILED": {
      action: "Object Detection Failed",
      initials: "OD",
      status: "error",
    },
    "YOUZU_API:LENS_SEARCH_STARTED": {
      action: "Started Youzu Lens search",
      initials: "YL",
      status: "success",
    },
    "YOUZU_API:LENS_SEARCH_SUCCEEDED": {
      action: "Completed Youzu Lens search",
      initials: "YL",
      status: "success",
    },
    "YOUZU_API:LENS_SEARCH_FAILED": {
      action: "Failed Youzu Lens search",
      initials: "YL",
      status: "error",
    },
    "YOUZU_API:PROPERTY_VISUALIZE_CREATE_STARTED": {
      action: "Started property visualization",
      initials: "PV",
      status: "success",
    },
    "YOUZU_API:PROPERTY_VISUALIZE_CREATE_SUCCEEDED": {
      action: "Completed property visualization",
      initials: "PV",
      status: "success",
    },
    "YOUZU_API:PROPERTY_VISUALIZE_CREATE_FAILED": {
      action: "Failed property visualization",
      initials: "PV",
      status: "error",
    },
    "YOUZU_API:PROPERTY_VISUALIZE_RESTYLE_SUCCEEDED": {
      action: "Property visualize restyle succeeded",
      initials: "YZ",
      status: "success",
    },
    "YOUZU_API:PROPERTY_VISUALIZE_CAMERA_VIEW_SUCCEEDED": {
      action: "Property visualize camera view succeeded",
      initials: "YZ",
      status: "success",
    },
    "YOUZU_API:PROPERTY_VISUALIZE_CLEAN_ROOM_SUCCEEDED": {
      action: "Property visualize clean room succeeded",
      initials: "YZ",
      status: "success",
    },
    "YOUZU_API:PROPERTY_VISUALIZE_RESTYLE_FAILED": {
      action: "Property visualize restyle failed",
      initials: "YZ",
      status: "error",
    },
    "YOUZU_API:PROPERTY_VISUALIZE_CAMERA_VIEW_FAILED": {
      action: "Property visualize camera view failed",
      initials: "YZ",
      status: "error",
    },
    "YOUZU_API:PROPERTY_VISUALIZE_CLEAN_ROOM_FAILED": {
      action: "Property visualize clean room failed",
      initials: "YZ",
      status: "error",
    },
    "YOUZU_API:ROOM_VISUALIZE_CREATE_STARTED": {
      action: "Started room visualization",
      initials: "RV",
      status: "success",
    },
    "YOUZU_API:ROOM_VISUALIZE_CREATE_SUCCEEDED": {
      action: "Completed room visualization",
      initials: "RV",
      status: "success",
    },
    "YOUZU_API:ROOM_VISUALIZE_CREATE_FAILED": {
      action: "Failed room visualization",
      initials: "RV",
      status: "error",
    },
    "YOUZU_API:BACKGROUND_REMOVAL_STARTED": {
      action: "Started background removal",
      initials: "BG",
      status: "success",
    },
    "YOUZU_API:BACKGROUND_REMOVAL_SUCCEEDED": {
      action: "Completed background removal",
      initials: "BG",
      status: "success",
    },
    "YOUZU_API:BACKGROUND_REMOVAL_FAILED": {
      action: "Failed background removal",
      initials: "BG",
      status: "error",
    },
  };

  return (
    eventMap[eventType] || {
      action: eventType
        .replace("YOUZU_API:", "")
        .replace(/_/g, " ")
        .toLowerCase(),
      initials: "YZ",
      status: eventType.includes("FAILED") ? "error" : "success",
    }
  );
}

function getRelativeTime(timestamp: string): string {
  const now = new Date();
  const past = new Date(timestamp);
  const diffMs = now.getTime() - past.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  return past.toLocaleDateString();
}

export function Activities({
  compact = false,
  events,
  filterEventType,
}: ActivitiesProps) {
  const { can } = useOrganization();
  // Fetching the activity feed needs the analytics permission; custom events
  // passed in by the parent don't touch the API.
  const canFetch = can("analytics");
  const [apiEvents, setApiEvents] = useState<AnalyticsEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const itemsPerPage = 10;

  useEffect(() => {
    // Don't fetch if we have custom events passed in
    if (events && events.length > 0) {
      setLoading(false);
      return;
    }
    if (!canFetch) {
      setLoading(false);
      return;
    }

    const fetchActivities = async () => {
      try {
        setLoading(true);
        const response = await getRecentActivity(
          itemsPerPage,
          1,
          filterEventType
        );

        setApiEvents(response.data);
        setError(null);
      } catch (err) {
        console.error("Failed to fetch recent activity:", err);
        setError("Failed to load recent activity");
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [canFetch]);

  const mapped = (events || []).map((e) => ({
    id: e.id,
    user: {
      initials: toInitials(e.user),
      name: e.user,
    },
    action: `${e.eventType}${e.details ? ": " + e.details : ""}`,
    timestamp: new Date(e.timestamp).toLocaleString(),
    status: mapStatus(e.status),
  }));

  const transformedApiEvents = apiEvents.map((event) => {
    const eventInfo = formatEventType(event.eventType);
    const label = event.metadata?.label || event.metadata?.name;
    return {
      id: event.id,
      user: {
        initials: eventInfo.initials,
        name: "System",
      },
      action: label ? `${eventInfo.action}: ${label}` : eventInfo.action,
      timestamp: getRelativeTime(event.timestamp),
      status: eventInfo.status,
    };
  });

  const source = events && events.length > 0 ? mapped : transformedApiEvents;
  const displayActivities = compact ? source.slice(0, 3) : source;

  if (!canFetch && !(events && events.length > 0)) {
    return <PermissionNotice feature="analytics" />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-sm text-muted-foreground">
          Loading activities...
        </div>
      </div>
    );
  }

  if (error && !apiEvents.length) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-sm text-muted-foreground">{error}</div>
      </div>
    );
  }

  if (!displayActivities.length) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-sm text-muted-foreground">No events found</div>
      </div>
    );
  }

  if (!compact) {
    return (
      <div className="space-y-5">
        <div className="space-y-5">
          {displayActivities.map((activity) => (
            <div key={activity.id} className="flex items-start gap-4">
              <Avatar className="h-10 w-10 border-2 border-background">
                <AvatarFallback className="bg-gradient-to-br from-pink-500 to-purple-500 text-white">
                  {activity.user.initials}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 space-y-1">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">{activity.action}</p>
                  <Badge
                    variant={
                      activity.status === "success"
                        ? "default"
                        : activity.status === "warning"
                        ? "outline"
                        : "destructive"
                    }
                    className={
                      activity.status === "success"
                        ? "bg-green-500 hover:bg-green-600"
                        : activity.status === "warning"
                        ? "border-amber-500 text-amber-500"
                        : ""
                    }
                  >
                    {activity.status === "success"
                      ? "Success"
                      : activity.status === "warning"
                      ? "Warning"
                      : "Error"}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  {activity.timestamp}
                </p>
              </div>
            </div>
          ))}
        </div>


      </div>
    );
  }

  // Compact version
  return (
    <div className="space-y-3">
      {displayActivities.map((activity) => (
        <div key={activity.id} className="flex items-center gap-3">
          <Avatar className="h-8 w-8 border-2 border-background">
            <AvatarFallback className="bg-gradient-to-br from-pink-500 to-purple-500 text-white text-xs">
              {activity.user.initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{activity.action}</p>
            <p className="text-xs text-muted-foreground">
              {activity.timestamp}
            </p>
          </div>
          <Badge
            variant={
              activity.status === "success"
                ? "default"
                : activity.status === "warning"
                ? "outline"
                : "destructive"
            }
            className={
              activity.status === "success"
                ? "bg-green-500 hover:bg-green-600"
                : activity.status === "warning"
                ? "border-amber-500 text-amber-500"
                : ""
            }
          >
            {activity.status === "success"
              ? "Success"
              : activity.status === "warning"
              ? "Warning"
              : "Error"}
          </Badge>
        </div>
      ))}
    </div>
  );
}
